import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, MessageCircle, PanelRightClose, PanelRightOpen, Pencil, Search, Send, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { MessageBubble } from "@/components/assistant/MessageBubble"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { useAllowanceNotice } from "@/components/generations/format"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cableConsumer } from "@/lib/cable"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { ChatMessage, Conversation, ConversationSummary } from "@/types/assistant"
import type { Allowance } from "@/types/generations"
import type { PageProps } from "@/types/inertia"

/** If no streamed update arrives for this long, reload the messages instead. */
const FALLBACK_POLL_MS = 4000

const SUGGESTIONS = [
  { id: "assistant.suggestion_ethics" },
  { id: "assistant.suggestion_gad" },
  { id: "assistant.suggestion_risk" },
  { id: "assistant.suggestion_cbt_dbt" },
]

/** For people who haven't unlocked a program: not tied to any one subject. */
const GENERAL_SUGGESTIONS = [{ id: "assistant.suggestion_concept" }, { id: "assistant.suggestion_plan" }]

type AssistantProps = {
  conversations: ConversationSummary[]
  conversation: Conversation | null
  messages: ChatMessage[]
  allowance: Allowance
  configured: boolean
}

export default function AssistantShow(props: AssistantProps) {
  const intl = useIntl()
  const { conversations, conversation } = props
  const title = conversation ? conversation.title : intl.formatMessage({ id: "assistant.title" })
  const programAccess = usePage<PageProps>().props.current_user?.program_access ?? false
  const description = programAccess ? intl.formatMessage({ id: "assistant.meta_program" }) : intl.formatMessage({ id: "assistant.meta_general" })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <HistoryLayout conversations={conversations} activeId={conversation?.id ?? null}>
          {conversation ? (
            <ChatView key={conversation.id} {...props} conversation={conversation} />
          ) : (
            <NewChat {...props} />
          )}
        </HistoryLayout>
      </AppShell>
    </>
  )
}

const HISTORY_KEY = "assistant-history-open"

/** Chat content on the left, collapsible chat history on the right. */
function HistoryLayout({
  conversations,
  activeId,
  children,
}: {
  conversations: ConversationSummary[]
  activeId: number | null
  children: React.ReactNode
}) {
  const intl = useIntl()
  const [open, setOpen] = React.useState(false)

  // Start collapsed; restore the saved choice after mount (keeps SSR markup stable).
  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(HISTORY_KEY)
      if (saved !== null) setOpen(saved === "1")
    } catch {}
  }, [])

  const toggle = () => {
    const next = !open
    setOpen(next)
    try {
      window.localStorage.setItem(HISTORY_KEY, next ? "1" : "0")
    } catch {}
  }

  return (
    <div className={cn("grid gap-6", open ? "lg:grid-cols-[minmax(0,1fr)_16rem]" : "lg:grid-cols-[minmax(0,1fr)_2.5rem]")}>
      <div className="min-w-0">{children}</div>
      <aside aria-label={intl.formatMessage({ id: "assistant.history_label" })} className="order-first min-w-0 lg:order-none lg:sticky lg:top-8 lg:self-start">
        <div className={cn("flex items-center gap-2", open ? "justify-between" : "lg:justify-center")}>
          <h2 className={cn("text-base", !open && "lg:hidden")}>{intl.formatMessage({ id: "assistant.history" })}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-expanded={open}
            aria-controls="chat-history"
            aria-label={open ? intl.formatMessage({ id: "assistant.collapse_history" }) : intl.formatMessage({ id: "assistant.expand_history" })}
            onClick={toggle}
          >
            {open ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
        </div>
        <div id="chat-history" hidden={!open}>
          <ConversationList conversations={conversations} activeId={activeId} />
        </div>
      </aside>
    </div>
  )
}

function ConversationList({ conversations, activeId }: { conversations: ConversationSummary[]; activeId: number | null }) {
  const intl = useIntl()
  const { date } = useFormat()
  const [query, setQuery] = React.useState("")
  const needle = query.trim().toLowerCase()
  const shown = needle ? conversations.filter((c) => c.title.toLowerCase().includes(needle)) : conversations

  return (
    <nav aria-label={intl.formatMessage({ id: "assistant.chats_label" })} className="mt-2">
      {conversations.length > 0 && (
        <div className="relative">
          <label htmlFor="chat-search" className="sr-only">
            {intl.formatMessage({ id: "assistant.search" })}
          </label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input
            id="chat-search"
            type="search"
            placeholder={intl.formatMessage({ id: "assistant.search" })}
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {shown.length > 0 && (
        <ul className="mt-3 space-y-1 p-0">
          {shown.map((c) => (
            <li key={c.id} className="list-none">
              <Link
                href={`/conversations/${c.id}`}
                aria-current={c.id === activeId ? "page" : undefined}
                className={cn(
                  "block rounded-md px-3 py-2 no-underline",
                  c.id === activeId ? "bg-accent-faded text-accent-display" : "text-ink-body hover:bg-surface",
                )}
              >
                <span className="block truncate text-sm">{c.title}</span>
                <span className="block text-xs text-ink-muted">{date(c.updated_on, { month: "short", day: "numeric" })}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {conversations.length === 0 && <p className="mt-3 text-sm text-ink-muted">{intl.formatMessage({ id: "assistant.no_chats" })}</p>}
      {conversations.length > 0 && shown.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted" role="status">
          {intl.formatMessage({ id: "assistant.no_match" }, { query: query.trim() })}
        </p>
      )}
    </nav>
  )
}

function NewChat({ allowance, configured }: AssistantProps) {
  const intl = useIntl()
  const programAccess = usePage<PageProps>().props.current_user?.program_access ?? false
  const suggestions = programAccess ? SUGGESTIONS : GENERAL_SUGGESTIONS
  const form = useForm({ text: "" })
  const send = (text: string) => {
    form.transform(() => ({ text }))
    form.post("/conversations")
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1>{intl.formatMessage({ id: "assistant.title" })}</h1>
      <p className="mt-2">
        {programAccess ? intl.formatMessage({ id: "assistant.new_intro_program" }) : intl.formatMessage({ id: "assistant.new_intro_general" })}
      </p>
      <div className="mt-6 grid gap-2 sm:grid-cols-2">
        {suggestions.map((s) => (
          <button
            key={s.id}
            type="button"
            disabled={form.processing || !configured}
            onClick={() => send(intl.formatMessage(s))}
            className="cursor-pointer rounded-xl border border-hairline bg-page px-4 py-3 text-left text-sm text-ink-body hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
          >
            {intl.formatMessage(s)}
          </button>
        ))}
      </div>
      <div className="mt-6">
        <Composer
          value={form.data.text}
          onChange={(text) => form.setData("text", text)}
          onSend={() => send(form.data.text)}
          busy={form.processing}
          allowance={allowance}
          configured={configured}
          error={form.errors.text}
          autoFocus
        />
      </div>
    </div>
  )
}

function ChatView({ conversation, messages: initialMessages, allowance, configured }: AssistantProps & { conversation: Conversation }) {
  const intl = useIntl()
  const [messages, setMessages] = React.useState(initialMessages)
  const [renaming, setRenaming] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [draft, setDraft] = React.useState("")
  const { props } = usePage<PageProps>()
  const endRef = React.useRef<HTMLDivElement>(null)
  const lastUpdate = React.useRef(Date.now())
  const replying = messages.some((m) => m.status === "streaming")

  React.useEffect(() => setMessages(initialMessages), [initialMessages])

  // Live reply text over Action Cable.
  React.useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null
    let cancelled = false
    cableConsumer().then((consumer) => {
      if (cancelled) return
      subscription = consumer.subscriptions.create(
        { channel: "ConversationChannel", id: conversation.id },
        {
          received: (data: { type: string; message: ChatMessage }) => {
            if (data.type !== "update") return
            lastUpdate.current = Date.now()
            setMessages((all) => {
              const exists = all.some((m) => m.id === data.message.id)
              return exists ? all.map((m) => (m.id === data.message.id ? data.message : m)) : [...all, data.message]
            })
          },
        },
      )
    })
    return () => {
      cancelled = true
      subscription?.unsubscribe()
    }
  }, [conversation.id])

  // Safety net if the live connection is unavailable: poll while a reply is in progress.
  React.useEffect(() => {
    if (!replying) return
    lastUpdate.current = Date.now()
    const timer = window.setInterval(() => {
      if (Date.now() - lastUpdate.current > FALLBACK_POLL_MS) router.reload({ only: ["messages", "allowance"] })
    }, FALLBACK_POLL_MS)
    return () => window.clearInterval(timer)
  }, [replying])

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" })
  }, [messages])

  const send = () => {
    const text = draft.trim()
    if (!text) return
    router.post(
      `/conversations/${conversation.id}/messages`,
      { text },
      {
        preserveScroll: true,
        onStart: () => setSending(true),
        onSuccess: () => setDraft(""),
        onFinish: () => setSending(false),
      },
    )
  }

  const started = conversation.starting_question

  return (
    <div className="mx-auto flex max-w-3xl flex-col">
      <Link href="/assistant" className="mb-3 inline-flex items-center gap-1 text-sm lg:hidden">
        <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "assistant.all_chats" })}
      </Link>
      <div className="flex items-start gap-2 border-b border-hairline pb-4">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <RenameForm conversation={conversation} onDone={() => setRenaming(false)} />
          ) : (
            <h1 className="break-words">{conversation.title}</h1>
          )}
          {started && (
            <p className="mt-1 text-sm text-ink-muted">
              <MessageCircle className="mr-1 inline h-3.5 w-3.5" />
              {started.kind === "exam"
                ? intl.formatMessage({ id: "assistant.about_exam" }, { source: started.source_title })
                : intl.formatMessage({ id: "assistant.about_quiz" }, { source: started.source_title })}
            </p>
          )}
        </div>
        {!renaming && (
          <>
            <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "assistant.rename_chat" })} onClick={() => setRenaming(true)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "assistant.delete_chat" })} onClick={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      <div className="mt-6 space-y-6" aria-live="polite">
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            message={m}
            conversationId={conversation.id}
            conversationTitle={conversation.title}
            showActions={i === messages.length - 1 && m.role === "assistant"}
          />
        ))}
        {/* Leaves room for the sticky composer when scrolling to the newest message. */}
        <div ref={endRef} className="scroll-mb-56" />
      </div>

      <div className="sticky bottom-0 mt-6 bg-page pb-4 pt-2">
        <Composer
          value={draft}
          onChange={setDraft}
          onSend={send}
          busy={sending || replying}
          busyLabel={replying ? intl.formatMessage({ id: "assistant.replying" }) : undefined}
          allowance={allowance}
          configured={configured}
          error={props.errors?.text}
        />
      </div>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={intl.formatMessage({ id: "assistant.delete_title" })}
        description={intl.formatMessage({ id: "assistant.delete_body" }, { title: conversation.title })}
        confirmLabel={intl.formatMessage({ id: "assistant.delete_chat" })}
        onConfirm={() => router.delete(`/conversations/${conversation.id}`)}
      />
    </div>
  )
}

function RenameForm({ conversation, onDone }: { conversation: Conversation; onDone: () => void }) {
  const intl = useIntl()
  const form = useForm({ title: conversation.title })
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        form.patch(`/conversations/${conversation.id}`, { preserveScroll: true, onSuccess: onDone })
      }}
    >
      <label htmlFor="chat-title" className="sr-only">
        {intl.formatMessage({ id: "assistant.chat_name" })}
      </label>
      <Input
        id="chat-title"
        className="min-w-0 flex-1"
        required
        maxLength={80}
        autoFocus
        value={form.data.title}
        onChange={(e) => form.setData("title", e.target.value)}
      />
      <Button type="submit" size="sm" disabled={form.processing}>
        {intl.formatMessage({ id: "common.save" })}
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={onDone}>
        {intl.formatMessage({ id: "common.cancel" })}
      </Button>
    </form>
  )
}

function Composer({
  value,
  onChange,
  onSend,
  busy,
  busyLabel,
  allowance,
  configured,
  error,
  autoFocus = false,
}: {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  busy: boolean
  busyLabel?: string
  allowance: Allowance
  configured: boolean
  error?: string
  autoFocus?: boolean
}) {
  const intl = useIntl()
  const atLimit = !allowance.unlimited && allowance.remaining === 0
  const allowanceNotice = useAllowanceNotice()
  const notice = allowanceNotice(allowance, "assistant")
  const disabled = busy || atLimit || !configured

  return (
    <div>
      {!configured && (
        <div className="callout callout-danger mb-3">
          <p>{intl.formatMessage({ id: "assistant.not_configured" })}</p>
        </div>
      )}
      {configured && notice && <p className={cn("mb-2 text-sm", atLimit ? "text-danger-display" : "text-signal-display")}>{notice}</p>}
      <form
        className="flex items-end gap-2 rounded-lg border border-hairline bg-page p-2 focus-within:border-accent"
        onSubmit={(e) => {
          e.preventDefault()
          if (!disabled) onSend()
        }}
      >
        <label htmlFor="chat-input" className="sr-only">
          {intl.formatMessage({ id: "assistant.message_label" })}
        </label>
        <textarea
          id="chat-input"
          rows={2}
          autoFocus={autoFocus}
          placeholder={busyLabel ?? intl.formatMessage({ id: "assistant.placeholder" })}
          className="max-h-48 min-h-12 flex-1 resize-none bg-transparent px-2 py-1.5 text-ink-display outline-none placeholder:text-ink-muted"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              if (!disabled && value.trim()) onSend()
            }
          }}
        />
        <Button type="submit" size="icon" aria-label={intl.formatMessage({ id: "assistant.send" })} disabled={disabled || !value.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
      {error && <p className="mt-2 text-xs text-danger-display">{error}</p>}
      <p className="mt-2 text-xs text-ink-muted">
        {intl.formatMessage({ id: "assistant.disclaimer" })}
      </p>
    </div>
  )
}
